import { router, usePage } from "@inertiajs/react"
import request from "react-rb/request"
import { usePageProps as useReactRbPageProps } from "react-rb"

// These are the routes that can be handled by Inertia. If you use regex, remember to use ^ to anchor the start of the string.
// ⚠️ IMPORTANT: When adding new routes, ensure the regex pattern doesn't accidentally match additional path segments
// For example, use [^/]* to match anything after the ID except forward slashes, allowing query params but preventing /edit, /new, etc.
const InertiaRoutes = [
  "/sequences", // Sequences index
  /^\/sequences\/new[^/]*$/, // Sequences new
  /^\/sequences\/[0-9]+[^/]*$/, // Sequences show page: /sequences/123 (negative lookahead to prevent additional slashes)
  /^\/sequences\/[0-9]+\/edit[^/]*$/, // Sequences edit page: /sequences/123/edit
  /^\/sequences\/[0-9]+\/subscribers[^/]*$/, // Sequences edit page: /sequences/123/subscribers
  /^\/publications\/[0-9]+\/reports\/(overview|analytics|clicks|recipients)$/, // Publication reports
  /^\/forms[^/]*$/, // Forms index
  /^\/forms\/formats[^/]*$/,
  /^\/forms\/new[^/]*$/,
  /^\/pages\/new[^/]*$/,
  /^\/pages\/preview\/[^/]*$/,
  /^\/forms\/designers\/new[^/]*$/,
  /^\/forms\/designers\/[0-9]+\/edit[^/]*$/,
  /^\/editor\/starting-points[^/]*$/,
  /^\/campaigns\/[0-9]+\/draft[^/]*$/,
  /^\/campaigns\/[0-9]+\/share[^/]*$/,
  /^\/campaigns\/new\/steps$/,
  /^\/subscribers[^/]*$/,
  /^\/subscribers\/performance[^/]*$/,
  /^\/automations[^/]*$/,
  /^\/automations\/templates[^/]*$/,
  /^\/automations\/[0-9]+\/nodes\/[0-9]+\/subscribers\/(completed|current|waiting)[^/]*$/,
  /^\/landing_pages\/[0-9]+\/subscriptions\/[^/]*$/,
  /^\/integrations\/gumroads[^/]*$/,
  /^\/creator-network[^/]*$/,
  /^\/creator-network\/subscribers[^/]*$/,
  /^\/creator-network\/recommendations[^/]*$/,
  /^\/creator-network\/discover[^/]*$/,
  /^\/creator-network\/customize[^/]*$/,
  /^\/creator-network\/contact-requests[^/]*$/,
  /^\/creator-network\/setup[^/]*$/,
  /^\/paid_recommendations[^/]*$/,
  /^\/paid_recommendations\/setup[^/]*$/,
  /^\/paid_recommendations\/discover[^/]*$/,
  /^\/ads\/onboarding(\?[^/]*)?$/,
  /^\/ads\/schedule(\?[^/]*)?$/,
  /^\/ads\/offers(\?[^/]*)?$/,
  /^\/ads\/performance\/insertion_offers\/[0-9]+\/edit[^/]*$/,
  /^\/ads\/performance\/insertion_offers\/[0-9]+[^/]*$/,
  /^\/ads\/reports(\?[^/]*)?$/,
  /^\/integrations\/patreons[^/]*$/,
  /^\/integrations\/facebooks[^/]*$/,
  /^\/integrations\/stripes[^/]*$/,
  /^\/sequences\/[0-9]+\/reports\/[0-9]+\/opens[^/]*$/,
  /^\/sequences\/[0-9]+\/reports\/[0-9]+\/unsubscribes[^/]*$/,
  /^\/sequences\/[0-9]+\/reports\/[0-9]+\/errors[^/]*$/,
  /^\/sequences\/[0-9]+\/reports\/[0-9]+\/recipients[^/]*$/,
  /^\/rules[^/]*$/,
  /^\/rules\/new[^/]*$/,
  /^\/rules\/[0-9]+\/edit[^/]*$/,
  /^\/apps[^/]*$/,
  /^\/apps\/[0-9]+[^/]*$/,
  /^\/apps\/[0-9]+\/edit[^/]*$/,
  /^\/apps\/[0-9]+\/auth[^/]*$/,
  /^\/apps\/[0-9]+\/plugins[^/]*$/,
  /^\/apps\/[0-9]+\/plugins\/[0-9]+[^/]*$/,
  /^\/apps\/[0-9]+\/distribution[^/]*$/,
  /^\/apps\/[0-9]+\/automation_templates[^/]*$/,
  /^\/apps\/[0-9]+\/automation_templates\/[0-9]+[^/]*$/,
  /^\/feeds[^/]*$/,
  /^\/admin\/ads\/performance\/brands[^/]*$/,
  /^\/admin\/ads\/performance\/brands\/new[^/]*$/,
  /^\/admin\/ads\/performance\/brands\/[0-9]+[^/]*$/,
  /^\/admin\/ads\/performance\/brands\/[0-9]+\/edit[^/]*$/,
  /^\/admin\/ads\/performance\/insertion_orders\/new[^/]*$/,
  /^\/admin\/ads\/performance\/insertion_orders\/[0-9]+[^/]*$/,
  /^\/admin\/ads\/performance\/insertion_orders\/[0-9]+\/edit[^/]*$/,
  /^\/admin\/apps[^/]*$/,
  /^\/admin\/apps\/featured[^/]*$/,
  /^\/admin\/apps\/featured\/[0-9]+[^/]*$/,
  /^\/editor\/templates\/[0-9]+\/edit[^/]*$/,
  /^\/dashboard[^/]*$/,
  /^\/$/,
  /^\/campaigns[^/]*$/,
  /^\/onboarding\/signup_survey[^/]*$/, // Onboarding survey root
  /^\/onboarding\/signup_survey\/(migrating|audience_size|about_you)[^/]*$/, // Onboarding survey steps
  /^\/onboarding\/adventures[^/]*$/, // Onboarding adventures
  /^\/onboarding\/business[^/]*$/, // Onboarding business
  /^\/adventures[^/]*$/ // Adventures root
]

export const canUseInertia = destinationPath => {
  const destinationUrl = new URL(
    destinationPath,
    `https://${process.env.DOMAIN}`
  )
  const destinationDomain = destinationUrl.hostname
  return (
    isInertiaPage() &&
    destinationDomain === process.env.DOMAIN &&
    InertiaRoutes.some(route =>
      typeof route === "string"
        ? route === destinationUrl.pathname
        : route.test(destinationUrl.pathname)
    )
  )
}

/**
 * Sets inertia page flag globally on the window,
 * so that it's accessible anywhere on the page.
 */
export function setInertiaPage() {
  window.app = window.app || {}
  window.app.inertia = true
}

/**
 * Removes inertia key from the window
 */
export function resetInertiaPage() {
  window.app = window.app || {}
  delete window.app.inertia
}

/**
 * Checks if current page is rendered using inertia.
 */
export function isInertiaPage() {
  return window.app?.inertia
}

/**
 * Simple navigation helper that automatically decides whether to use inertia or not.
 */
export function navigate(url, options = {}) {
  canUseInertia(url) ? router.visit(url, options) : (window.location.href = url)
}

/**
 * Performs page reload, not only props refresh
 */
export function reloadPage({ onFinish, preserveScroll = false } = {}) {
  if (isInertiaPage()) {
    router.visit(window.location.href, {
      preserveState: false,
      preserveScroll,
      onFinish
    })
  } else {
    window.location.reload()
  }
}

/**
 * Base helper for making HTTP requests with consistent callback handling
 */
function makeRequest(url, method, data, options = {}) {
  const {
    onError = () => {},
    onSuccess = () => {},
    onFinish = () => {}
  } = options

  request(url, {
    method,
    data
  })
    .then(result => {
      if (!result.response.ok) {
        onError(result)
      } else {
        onSuccess(result)
      }
    })
    .catch(error => {
      onError(error)
    })
    .finally(() => {
      onFinish()
    })
}

/**
 * Performs a POST request and handles redirects
 */
export function post(
  url,
  data,
  options = { onError: () => {}, onSuccess: () => {}, onFinish: () => {} }
) {
  if (canUseInertia(url)) {
    router.post(url, data, {
      ...options,
      onSuccess: () => {
        options?.onSuccess?.()
      },
      onError: error => {
        options?.onError?.(error)
      },
      onFinish: () => {
        options?.onFinish?.()
      }
    })
  } else {
    makeRequest(url, "POST", data, options)
  }
}

/**
 * Performs a PUT request and handles redirects
 */
export function put(
  url,
  data,
  options = { onError: () => {}, onSuccess: () => {}, onFinish: () => {} }
) {
  if (canUseInertia(url)) {
    router.put(url, data, {
      ...options,
      onSuccess: () => {
        options?.onSuccess?.()
      },
      onError: error => {
        options?.onError?.(error)
      },
      onFinish: () => {
        options?.onFinish?.()
      }
    })
  } else {
    makeRequest(url, "PUT", data, options)
  }
}

/**
 * Allows to reload some props in the background.
 * The nonInertiaRefresh param is temporary to help with the migration,
 * it should be the react-rb navigation.refresh function
 */
export function reloadProps({ only, nonInertiaRefresh, onFinish } = {}) {
  if (isInertiaPage()) {
    only ? router.reload({ only, onFinish }) : router.reload({ onFinish })
  } else {
    nonInertiaRefresh && nonInertiaRefresh()
  }
}

/**
 * Updates the query parameters of the current URL without a full page reload.
 *
 * This function constructs a new URL by taking the current window's URL and replacing
 * its search part with the provided `queryParams`.
 *
 * The behavior for updating the browser's history and address bar depends on whether
 * the current page is an Inertia page:
 * - For Inertia pages: It uses `router.push` with `preserveState: true` and
 *   `preserveScroll: true`. This updates the URL via Inertia, aiming to keep the
 *   client-side state and scroll position intact.
 * - For non-Inertia pages: It uses `window.history.pushState({}, "", url)` to update the URL.
 *   This directly manipulates the browser's history API, changing the URL in the
 *   address bar without triggering a page reload or an Inertia visit. This is useful
 *   for syncing URL parameters with client-side state without fetching new page data.
 *
 * @param {URLSearchParams} queryParams - An instance of `URLSearchParams` containing
 *   the new query parameters to apply to the current URL.
 */
export function updateQueryParams(queryParams) {
  const url = new URL(window.location.href)
  url.search = queryParams.toString()
  if (isInertiaPage()) {
    router.push({
      url: url.href,
      preserveState: true,
      preserveScroll: true
    })
  } else {
    window.history.pushState({}, "", url)
  }
}

/**
 * Returns the top level page props
 */
export function usePageProps() {
  const reactRbPageProps = useReactRbPageProps()
  const inertiaPageProps = useInertiaPageProps()

  if (isInertiaPage()) {
    return inertiaPageProps
  } else {
    return reactRbPageProps
  }
}

// inertia's hook raises an error when not on inertia page
function useInertiaPageProps() {
  try {
    return usePage()?.props
  } catch {
    return null
  }
}
